import React from 'react';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoveDown as MoveToBottomIcon,
  RotateRight as RotateRightIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

interface PhotoActionsProps {
  onEdit: () => void;
  onDelete: () => void;
  onMoveToBottom?: () => void;
  onRotate?: () => void;
  onSaveRotation?: () => void;
  pendingRotation?: number;
  isSavingRotation?: boolean;
  isDeleting?: boolean;
}

const PhotoActions: React.FC<PhotoActionsProps> = ({
  onEdit,
  onDelete,
  onMoveToBottom,
  onRotate,
  onSaveRotation,
  pendingRotation = 0,
  isSavingRotation = false,
  isDeleting = false,
}) => {
  const hasPendingRotation = pendingRotation > 0;
  const isLocked = isSavingRotation || isDeleting;
  // While rotation is pending, disable everything except rotate and save-rotation
  const isOtherActionsDisabled = isLocked || hasPendingRotation;

  return (
    <Box sx={styles.container}>
      {onMoveToBottom && (
        <Tooltip title='Move to bottom'>
          <IconButton
            size='small'
            onClick={e => {
              e.stopPropagation();
              onMoveToBottom();
            }}
            disabled={isOtherActionsDisabled}
            sx={styles.moveButton}
          >
            <MoveToBottomIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      )}
      {onSaveRotation && hasPendingRotation && (
        <Tooltip title={isSavingRotation ? 'Saving...' : 'Save rotation'}>
          <IconButton
            size='small'
            onClick={e => {
              e.stopPropagation();
              onSaveRotation();
            }}
            disabled={isSavingRotation}
            sx={styles.saveRotationButton}
          >
            {isSavingRotation ? <CircularProgress size={16} color='inherit' /> : <SaveIcon fontSize='small' />}
          </IconButton>
        </Tooltip>
      )}
      {onRotate && (
        <Tooltip title='Rotate right'>
          <IconButton
            size='small'
            onClick={e => {
              e.stopPropagation();
              onRotate();
            }}
            disabled={isLocked}
            sx={styles.rotateButton}
          >
            <RotateRightIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title='Edit photo'>
        <IconButton
          size='small'
          onClick={e => {
            e.stopPropagation();
            onEdit();
          }}
          disabled={isOtherActionsDisabled}
          sx={styles.editButton}
        >
          <EditIcon fontSize='small' />
        </IconButton>
      </Tooltip>
      <Tooltip title={isDeleting ? 'Deleting...' : 'Delete photo'}>
        <IconButton
          size='small'
          onClick={e => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={isOtherActionsDisabled}
          sx={{
            ...styles.deleteButton,
            color: isOtherActionsDisabled ? 'text.disabled' : 'error.main',
            '&:hover': {
              backgroundColor: isOtherActionsDisabled ? 'rgba(255, 255, 255, 0.9)' : 'error.50',
            },
            '&:disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
            },
          }}
        >
          {isDeleting ? <CircularProgress size={16} color='inherit' /> : <DeleteIcon fontSize='small' />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const styles = {
  container: {
    position: 'absolute' as const,
    top: 8,
    right: 8,
    display: 'flex',
    gap: 0.5,
  },
  moveButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: 'info.main',
    '&:hover': {
      backgroundColor: 'info.50',
    },
  },
  rotateButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: 'success.main',
    '&:hover': {
      backgroundColor: 'success.50',
    },
  },
  saveRotationButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: 'warning.main',
    '&:hover': {
      backgroundColor: 'warning.50',
    },
    '&:disabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
    },
  },
  editButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: 'warning.main',
    '&:hover': {
      backgroundColor: 'warning.50',
    },
  },
  deleteButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
};

export default PhotoActions;
